import { Mapper } from 'src/app/base/mapper';
import { IBranchEntity } from '../entity/branch.entity';
import {
  IBranchModel,
  isOpenEnum,
  qualitiesEnum,
} from '../models/branch.model';
import { IScheduleModel } from '../models/schedule.model';
import { IScheduleEntity } from '../entity/schedule.entity';

export class BranchImplementationRepositoryMapper extends Mapper<
  IBranchEntity,
  IBranchModel
> {
  mapFrom(param: IBranchEntity): IBranchModel {
    const address = this.buildAddress(param);

    return {
      id: param.id ?? 0,
      name: param.branchName ?? '',
      avatar: param.avatar ?? '',
      latitude: this.ensureCoordinateString(param.latitude),
      longitude: this.ensureCoordinateString(param.longitude),
      distance: 0,
      open: param.isOpen ? isOpenEnum.OPEN : isOpenEnum.CLOSE,
      qualities: this.extractQualities(param),
      address,
      schedules: {
        monday: this.getScheduleEntity('monday', param),
        tuesday: this.getScheduleEntity('tuesday', param),
        wednesday: this.getScheduleEntity('wednesday', param),
        thursday: this.getScheduleEntity('thursday', param),
        friday: this.getScheduleEntity('friday', param),
        saturday: this.getScheduleEntity('saturday', param),
        sunday: this.getScheduleEntity('sunday', param),
      },
    };
  }

  mapTo(param: IBranchModel): IBranchEntity {
    return {
      id: param.id,
      branchName: param.name,
      avatar: param.avatar,
      latitude: param.latitude,
      longitude: param.longitude,
      distance: 0,
      isOpen: param.open === isOpenEnum.OPEN ? true : false,
      isWifi: this.hasQuality(qualitiesEnum.WIFI, param),
      isMall: this.hasQuality(qualitiesEnum.MALL, param),
      isParking: this.hasQuality(qualitiesEnum.PARKING, param),
      isReclusa: this.hasQuality(qualitiesEnum.RECLUSA, param),
      isPrivateLivingRoom: this.hasQuality(
        qualitiesEnum.PRIVATE_LIVING_ROOM,
        param
      ),
      hourAvailable: 0,
      festivities: [],
      addresses: {
        street: param.address.street,
        sublocality: param.address.sublocality,
        borough: param.address.borough,
        locality: param.address.locality,
        state: param.address.state,
        zipCode: param.address.zipCode,
        phone: param.address.phone,
        whatsapp: param.address.whatsapp,
        latitude: param.address.latitude,
        longitude: param.address.longitude,
      },
      officeHours: {
        monday: this.getScheduleModel('monday', param),
        tuesday: this.getScheduleModel('tuesday', param),
        wednesday: this.getScheduleModel('wednesday', param),
        thursday: this.getScheduleModel('thursday', param),
        friday: this.getScheduleModel('friday', param),
        saturday: this.getScheduleModel('saturday', param),
        sunday: this.getScheduleModel('sunday', param),
      },
    };
  }

  private buildAddress(param: IBranchEntity) {
    return {
      street: param.addresses?.street ?? '',
      sublocality: param.addresses?.sublocality ?? '',
      borough: param.addresses?.borough ?? '',
      locality: param.addresses?.locality ?? '',
      state: param.addresses?.state ?? '',
      zipCode: this.toNumber(param.addresses?.zipCode),
      phone: param.addresses?.phone ?? '',
      whatsapp: param.addresses?.whatsapp ?? '',
      latitude: param.addresses?.latitude ?? 0,
      longitude: param.addresses?.longitude ?? 0,
      city: param.addresses?.city ?? '',
    };
  }

  private extractQualities(param: IBranchEntity): qualitiesEnum[] {
    const qualities: qualitiesEnum[] = [];

    if (param.isWifi) qualities.push(qualitiesEnum.WIFI);
    if (param.isMall) qualities.push(qualitiesEnum.MALL);
    if (param.isParking) qualities.push(qualitiesEnum.PARKING);
    if (param.isReclusa) qualities.push(qualitiesEnum.RECLUSA);
    if (param.isPrivateLivingRoom)
      qualities.push(qualitiesEnum.PRIVATE_LIVING_ROOM);

    return qualities;
  }

  hasQuality(quality: qualitiesEnum, param: IBranchModel) {
    return param.qualities.includes(quality);
  }

  getScheduleModel(day: keyof IScheduleModel, param: IBranchModel) {
    return {
      isClose: param.schedules[day]?.open ?? false,
      hourStart: param.schedules[day]?.start ?? '',
      hourEnd: param.schedules[day]?.end ?? '',
    };
  }

  getScheduleEntity(day: keyof IScheduleEntity, param: IBranchEntity) {
    return {
      open: param.officeHours?.[day]?.isClose ?? false,
      start: param.officeHours?.[day]?.hourStart ?? '',
      end: param.officeHours?.[day]?.hourEnd ?? '',
    };
  }

  private ensureCoordinateString(
    value: string | number | null | undefined
  ): string {
    if (value === null || value === undefined) {
      return '0';
    }

    return String(value);
  }

  private toNumber(value: unknown): number {
    if (value === null || value === undefined || value === '') {
      return 0;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
}
